import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import momentTz from 'moment-timezone';
import { GetFormatForMoment } from 'Core/data/Helpers';
import { unitSelectors } from 'Core/realtime/redux/unitSlice';

/** ISO instant → text in the given zone/locale; undefined for an unparsable value. */
const formatInstant = (iso, zone, format, locale) => {
	const instant = momentTz(iso);
	if (!instant.isValid()) return undefined;
	return instant.tz(zone).locale(locale).format(format);
};

/**
 * What the pure row builders and formatters need: `t(key, values)` keeps
 * z1-ui-main's translate signature over react-intl, `locale` drives number
 * formatting, `isKph` the unit system, and `formatDateTime` renders an ISO
 * instant in the unit's time zone (falling back to the user's), in the user's
 * date format — the same zone rule Vehicle Trails uses (`vehicle.timeZone.name || timeZone`).
 */
export const useOemFormatContext = (unitId) => {
	const intl = useIntl();
	const language = useSelector((state) => state.commomRedux?.language);
	const user = useSelector((state) => state.securityRedux?.user);
	const locale = language || intl.locale;
	const isKph = Boolean(user?.isKph);
	const unitTimeZone = useSelector((state) => {
		if (unitId === undefined || unitId === null || !state.realtime?.unit) return undefined;
		return unitSelectors.selectById(state, String(unitId))?.timeZone?.name;
	});
	const timeZone = unitTimeZone || user?.timeZone;
	const dateformat = user?.dateformat;

	return useMemo(() => {
		const t = (key, values) => intl.formatMessage({ id: key }, values);
		const zone = timeZone || momentTz.tz.guess();
		const defaultFormat = `${GetFormatForMoment(dateformat)} h:mm:ss A`;
		const formatDateTime = (iso, format = defaultFormat) => formatInstant(iso, zone, format, locale);
		return { t, locale, isKph, formatDateTime };
	}, [intl, locale, isKph, timeZone, dateformat]);
};
